import { defineStore } from 'pinia';
import { setCookie } from "../utils/cookie";
import { getCookieValue } from "../utils/cookie";
import http from "../../common/utils/http-common";
import router from "../utils/router";
import { combinedSchema } from "../utils/combinedSchema";
import { ValidationError } from 'yup';
import { priceRate } from "../../common/utils/pricing";
import * as Sentry from '@sentry/vue';
import dayjs from 'dayjs';
import { isVonoviaBlockedEmail, DELIVERY_TYPE } from '../utils/constants';

export const useTenantStore = defineStore('tenant', {
    state: () => ({
        priceRateString: priceRate.toString() + '%',
        contributionRate: priceRate / 100,
        minRate: 39,
        email: '',
        state: '',
        mappedState: '',
        product: '',
        orderId: '',
        hashId: '',
        isReservation: false,
        alteosAcceptedAt: '',
        documents: [],
        userExists: false,
        prefixedOrderId: '',
        reservationValid: false,
        partnerId: '',
        externalId: '',
        userData: null,
        isCreditCheckValid: false,
        creditCheckValidUntil: null,
        redirectionPath: '',
        redirectionType: '',  // 'internal', 'external' oder 'retry'
        processing: {
            startState: null, //can be null, 'customer_reservation', 'alteos_accepted'
            isIbanModalOpen: false,
            tryCount: 0,
            requestedState: null, // can be 'customer_reservation', 'alteos_accepted', 'finished'
            isProcessingModalOpen: false,
            showSpinnerOne: false,
            showSpinnerTwo: false,
            showSpinnerThree: false,
        },
        formValues: {
            start_gender: '',
            start_firstName: '',
            start_lastName: '',
            start_birthdate: '',
            start_email: '',
            start_phone: '',
            start_streetName: '',
            start_houseNumber: '',
            start_addressCode: '',
            start_city: '',
            start_termsAccepted: '',
            next_moveInDate: '',
            next_contractStart: '',
            next_propertyStreetName: '',
            next_propertyHouseNumber: '',
            next_propertyAddressCode: '',
            next_propertyCity: '',
            next_landlordStreetName: '',
            next_landlordHouseNumber: '',
            next_landlordAddressCode: '',
            next_landlordCity: '',
            next_landlordName: '',
            next_landlordEmail: '',
            next_rentAmount: '',
            next_depositAmount: '',
            next_IBAN: '',
            next_sepaTerms: '',
            next_destAddress: DELIVERY_TYPE.DIGITAL,
            tenantAddress: '',
        },
        errors: {
        },
        tracking: {
            trackedEcommerce: false,
            trackedReservation: false,
            trackedSofortzusage: false,
            trackedAblehnung: false,
            trackedPending: false,
        },
    }),
    getters: {
        getHashId(state) { // get the hashId from the state, cookie or local Storage
            if (state.hashId) {
                return state.hashId;
            }
            const hashIdFromCookie = getCookieValue('hashId');
            if (hashIdFromCookie) {
                return hashIdFromCookie;
            }
            const hashIdFromLocalStorage = localStorage.getItem('id');
            if (hashIdFromLocalStorage) {
                return hashIdFromLocalStorage;
            }

            // Wenn keine hashId gefunden wurde
            return null;
        },
        getIsReservation(state) {
            if (state.isReservation) {
                return state.isReservation;
            }
            const isReservationFromLocalStorage = window.localStorage.getItem('isReservation');
            if (isReservationFromLocalStorage !== null) {
                if (isReservationFromLocalStorage === 'true') {
                    return true;
                }
            }
            return false;
        },
        depositAmountAsNumber(state) {
            return this.parseLocalizedNumber(state.formValues.next_depositAmount);
        },
        rentAmountAsNumber(state) {
            return this.parseLocalizedNumber(state.formValues.next_rentAmount);
        },
        requestedState() {
            let path = window.location.pathname;

            switch (path) {
                case '/app/start-buergschaft':
                    return 'alteos_accepted';
                case '/app/start-reservierung':
                    return 'customer_reservation';
                case '/app/next':
                    return 'finished';
                default:
                    return null;
            }
        },
        yearlyRate(state) { // calculate the yearly rate and return as number
            const deposit = this.parseLocalizedNumber(state.formValues.next_depositAmount);

            if (deposit === 0) {
                return "...";
            }
            const price = deposit*state.contributionRate;
            let yearlyRate = price < state.minRate ? state.minRate : price;
            yearlyRate = (Math.round(yearlyRate * 100)/100).toFixed(2); // round to 2 decimal places
            return  yearlyRate
        },
        monthlyRate(state) { // calculate the monthly rate and return as number
            if (isNaN(state.yearlyRate)) {
                return "...";
            }
            return  state.yearlyRate / 12;
        },
        yearlyRateString(state) {
            if (isNaN(state.yearlyRate)) {
              return "...";
            } 
            const rate = (Math.round(state.yearlyRate * 100) / 100).toFixed(2);
            return new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'}).format(rate).toString();
        },
        monthlyRateString(state) { // format the monthly rate and return as string de currency
            if (isNaN(state.monthlyRate)) {
                return "...";
            }
            const rate = (Math.round(state.monthlyRate * 100) / 100).toFixed(2);
            return new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'}).format(rate).toString();
        },
        formattedPropertyAddress: (state) => {
          const segments = [
            state.formValues.next_propertyStreetName,
            state.formValues.next_propertyHouseNumber,
            state.formValues.next_propertyAddressCode,
            state.formValues.next_propertyCity,
          ].filter(Boolean); // Entfernt leere oder undefined Werte
    
          const formattedAddress = segments.join(' ').trim();
          return formattedAddress || 'Bitte geben Sie Ihre Adresse oben im Formular an.';
        },
        formattedLandlordAddress: (state) => {
            const segments = [
                state.formValues.next_landlordStreetName,
                state.formValues.next_landlordHouseNumber,
                state.formValues.next_landlordAddressCode,
                state.formValues.next_landlordCity,
            ].filter(Boolean); // Entfernt leere oder undefined Werte
            
            const formattedAddress = segments.join(' ').trim();
            return formattedAddress || 'Bitte geben Sie die Adresse Ihres Vermieters oben im Formular an.';
        },
        formattedTenantAddress: (state) => {
            const segments = [
                state.formValues.start_streetName,
                state.formValues.start_houseNumber,
                state.formValues.start_addressCode,
                state.formValues.start_city,
            ].filter(Boolean); // Entfernt leere oder undefined Werte

            const formattedAddress = segments.join(' ').trim();
            return formattedAddress || 'Bitte geben Sie Ihre Adresse oben im Formular an.';
        },
        tenantAddress(state) {
            return {
              houseNumber: state.formValues.start_houseNumber,
              streetName: state.formValues.start_streetName,
              addressCode: state.formValues.start_addressCode,
              city: state.formValues.start_city,
            };
        },
        landlordAddress(state) {
            if(state.formValues.next_destAddress === DELIVERY_TYPE.LANDLORD) {
                return {
                    houseNumber: state.formValues.next_landlordHouseNumber,
                    streetName: state.formValues.next_landlordStreetName,
                    addressCode: state.formValues.next_landlordAddressCode,
                    city: state.formValues.next_landlordCity,
                };
            }
            return null;
        },
        propertyAddress(state) {
            return {
                houseNumber: state.formValues.next_propertyHouseNumber,
                streetName: state.formValues.next_propertyStreetName,
                addressCode: state.formValues.next_propertyAddressCode,
                city: state.formValues.next_propertyCity,
            };
        },
        preparedStartData(state) {
            return {
              firstName: state.formValues.start_firstName,
              lastName: state.formValues.start_lastName,
              birthdate: state.formValues.start_birthdate,
              gender: state.formValues.start_gender,
              email: state.formValues.start_email,
              phone: state.formValues.start_phone,
              termsAccepted: state.formValues.start_termsAccepted === '1',
              requestedState: state.processing.requestedState || state.requestedState,
              tenantAddress: state.tenantAddress, // Nutzt den tenantAddress Getter für die Adresse
              partnerId: state.partnerId,
              externalId: state.externalId,
            };
        },
        preparedNextData(state) {
            return {
              rentAmount: this.parseLocalizedNumber(state.formValues.next_rentAmount).toString(),
              moveInDate: state.formValues.next_moveInDate,
              contractStart: state.formValues.next_contractStart,
              landlordName: state.formValues.next_landlordName,
              landlordEmail: isVonoviaBlockedEmail(state.formValues.next_landlordEmail)
                  ? null
                  : state.formValues.next_landlordEmail?.trim() || null,
              IBAN: state.formValues.next_IBAN,
              depositAmount: this.parseLocalizedNumber(state.formValues.next_depositAmount).toString(),
              destAddress: state.formValues.next_destAddress,
              product: "hsde_04_2021",
              landlordAddress: state.landlordAddress,
              propertyAddress: state.propertyAddress,
              sepaTerms: state.formValues.next_sepaTerms === '1',
              contactAllowed: true,
              requestedState: state.processing.requestedState || state.requestedState,
            };
        },
        reservationOlderThan10Minutes(state) {
            const acceptedAt = new Date(state.alteosAcceptedAt);
            if (!state.alteosAcceptedAt || isNaN(acceptedAt.getTime())) {
                // Fallback, wenn keine Zeit gesetzt ist oder das Datum ungültig ist
                return true;
            }

            const now = new Date();
            const diff = (now - acceptedAt) / 60000;  // Differenz in Minuten
            return diff > 10 ;
        },
        reservationRoute() {
            return this.reservationOlderThan10Minutes ? '/app/next' : '/app/danke-reservierung';
        },
        showDestinationPossiblities(state) {
            if (state.formValues.next_destAddress === DELIVERY_TYPE.DIGITAL) {
                return false;
            }
            return true;
        },
        warnings(state) {
            const w = {};

            // next_contractStart: Warning wenn Einzug heute oder morgen
            const moveInDate = state.formValues.next_moveInDate;
            if (moveInDate && dayjs(moveInDate).isBefore(dayjs().startOf('day').add(2, 'day'), 'day')) {
                w['next_contractStart'] = 'Wenn Sie bereits eingezogen sind oder morgen einziehen, beginnt der Vertrag automatisch am nächsten Tag.';
            }

            return w;
        },
    },
    actions: {     
        prefillUserData() { // take the data from the userData/Backend or local Storage and fill the formValues
            const userData = this.userData;
            // Funktion zur Ermittlung historischer Daten
            const getHistoricalData = (item) => {
                let value = userData ? userData[item] : null;
                if (value === null || value === undefined || value === '0.00') {
                    value = null;
                }
                const localStorageValue = window.localStorage.getItem(item);
                return value ?? localStorageValue;
            };

            // Durchlaufen aller formValues und Vorausfüllen der Felder
            Object.keys(this.formValues).forEach((key) => {
                const value = getHistoricalData(key);
                if (value !== null && key !== 'tenantAddress') {
                    this.setFormValue(key, value);
                    this.validateField(key, value);
                }
            });
        },     
        async loadUserData(id) { // load the data from the server
            
            const hashId = id || this.getHashId;

            if (!hashId) {
              return;
            }
        
            this.setHashId(hashId); // Save the hashId in the store, local Storage and cookie
            
            Sentry.addBreadcrumb({
                category: 'api',
                message: `Requesting submit application for ${hashId} to load user data`,
                level: 'info'
            });

            try {
              const response = await http.get(`/api/submit-applications/${hashId}`);
              const data = response.data;
                  // Aktualisieren der Store-Zustände mit den API Daten
                this.email = data.email || this.email;
                this.state = data.state || this.state;
                this.mappedState = data.mappedState || this.mappedState;
                this.product = data.product || this.product;
                this.orderId = data.orderId || this.orderId;
                this.hashId = data.hashId || this.hashId;
                this.alteosAcceptedAt = data.alteosAcceptedAt || this.alteosAcceptedAt;
                this.documents = data.documents || this.documents;
                this.userExists = data.userExists !== undefined ? data.userExists : this.userExists;
                this.prefixedOrderId = data.prefixedOrderId || this.prefixedOrderId;
                this.reservationValid = data.reservationValid !== undefined ? data.reservationValid : this.reservationValid;
                this.partnerId = data.partnerId || this.partnerId;
                this.externalId = data.externalId || this.externalId;
              
              this.userData = data;
            
            } catch (error) {
                const responseData = error.response?.data || null;

                Sentry.captureException(error, {    
                    level: 'error',
                    tags: {
                        function: 'loadUserData',
                        hashId
                    },
                    extra: {
                        hashId,
                        responseData
                    }
                });
            }
        },
        determineRedirection() { // determine the redirection path based on the state
            const state = this.userData?.state;
            
            if (!state) { //maybe we should set /app/start as default
                return;
            }
            switch (state) {
                case 'submitted':
                case 'creditcheck_submit':
                case 'documents_pending':
                case 'policy_id_pending':
                case 'customer_accepted':
                    this.redirectionType = 'pending';
                    break;
                case 'alteos_accepted':
                    this.redirectionType = 'internal';
                    this.redirectionPath = '/app/next';
                    break;
                case 'reservation_issued':
                    this.redirectionType = 'internal';
                    this.redirectionPath = this.reservationRoute;
                    break;
                case 'finished':
                    this.redirectionType = 'internal';
                    this.redirectionPath = '/app/danke-guarantee';
                    break;
                case 'alteos_rejected':
                    this.redirectionType = 'internal';
                    this.redirectionPath = '/app/sorry';
                    break;
                case 'error':
                case 'error_critical':
                    this.redirectionType = 'external';
                    this.redirectionPath = '/error';
                    break;
                default:
                    this.redirectionPath = '/app/start';
                    this.redirectionType = 'internal';
                    break;
            }
        },
        async handlePendingState() {
            for (let attempts = 0; attempts < 30; attempts++) {
                this.determineRedirection(); // sets redirectionPath and redirectionType based on state

                // PF-9943: when the API response includes a one-shot login URL,
                // the application has reached a terminal state AND the applicant
                // is a brand-new account. Redirect to the login URL —
                // MagicLinkAuthenticator validates it server-side and
                // AuthenticationSuccessHandler lands the customer on the matching
                // success page (via _target_path) already authenticated.
                if (this.userData?.loginUrl) {
                    window.location.href = this.userData.loginUrl;
                    return;
                }

                if (this.redirectionType !== 'pending' && this.processing.startState !== this.state) {
                  break; // Wenn der Status nicht 'pending' ist, Schleife verlassen
                }
                await new Promise(resolve => setTimeout(resolve, 1000)); // 30 Sekunden warten
                await this.loadUserData(); // Erneutes Laden der Daten
              }

              if (this.redirectionType === 'pending') {
                this.trackPending();
                window.location.pathname ='/pending'; // Weiterleiten zu einer Pending-Seite
              } else {
                this.resetProcessing();
                this.navigate(); // Normale Navigation basierend auf `redirectionPath` und `redirectionType`
              }
        },
        navigate() {
            this.determineRedirection();
            const currentPath = window.location.pathname;
            this.trackFormSubmits();
            if (currentPath === this.redirectionPath) {
                return;
            }
            if (this.redirectionType === 'pending') {
                window.location.pathname = '/pending';
                return;
            }
            if (this.redirectionType === 'internal') {
                router.push(this.redirectionPath);
            } else if (this.redirectionType === 'external') {
                window.location.pathname = this.redirectionPath;
            } else {
                return false;
            }
        },
        stopStatusQuery() { // @toDo: stop the status query is not Working in all cases
            let path = window.location.pathname
            if (path === '/app/start-reservierung' || path === '/app/start-buergschaft') {
                window.location.pathname = '/app/next';
            } else if (path === '/app/next') {
                window.location.pathname = '/app/danke-guarantee';
            }
        },
        async validateField(fieldId, value) {
            // Bestimme den Schema-Abschnitt basierend auf dem Präfix des Feldnamens
            let schemaSection = this.getFieldStep(fieldId);
        
            if (!schemaSection) {
                Sentry.captureMessage(`Unknown schema section for field: ${fieldId}`, {
                    level: 'warning',
                    extra: {
                        fieldId,
                        value
                    }
                });
                return;
            }
        
            try {
                const tempFormValues = { ...this.formValues[schemaSection], [fieldId]: value };
                await combinedSchema.fields[schemaSection].validateAt(fieldId, tempFormValues);
                this.errors[fieldId] = undefined; // Fehler löschen, falls Validierung erfolgreich
            } catch (error) {
                if (error instanceof ValidationError) {
                    this.errors[fieldId] = error.message; // Fehler setzen
                } else {
                    Sentry.captureException(error, {
                        level: 'error',
                        tags: {
                            function: 'validateField',
                            fieldId
                        },
                        extra: {
                            fieldId,
                            value
                        }
                    });
                }
            }
        },     
        async validateSection(section) {
            try {
              await combinedSchema.fields[section].validate(this.formValues, { abortEarly: false });
              this.errors = {}; // Clear errors if validation passes
              return true;
            } catch (error) {
              if (error instanceof ValidationError) {
                // Transform Yup errors into a more frontend-friendly format
                this.errors = error.inner.reduce((acc, curr) => {
                  acc[curr.path] = curr.message;
                  return acc;
                }, {});
              } else {
                Sentry.captureException(error, {
                    level: 'error',
                    tags: {
                        function: 'validateSection',
                        section
                    },
                    extra: {
                        formValues: this.formValues
                    }
                });
                this.errors = { general: 'Ein unerwarteter Validierungsfehler ist aufgetreten.' };
              }
              return false;
            }
        },
        async postStartData() {
            const startData = this.preparedStartData;
            this.setProcessingRequestedState(startData.requestedState);
            
            Sentry.addBreadcrumb({
                category: 'api',
                message: `Requesting submit application with ${startData} to create user data`,
                level: 'info'
            });
            
            try {
                const response = await http.post('/api/submit-applications', startData);
                this.setHashId(response.data.hashId);
                this.setPrefixedOrderId(response.data.prefixedOrderId);
            } catch (error) {
                const responseData = error.response?.data || null;
                Sentry.captureException(error, {
                    level: 'error',
                    tags: {
                        function: 'postStartData'
                    },
                    extra: {
                        startData,
                        responseData
                    }
                });
                throw error;
          }
        },
        async postNextData(nextData) {
            const data = nextData || this.preparedNextData;
            const hashId = this.getHashId;
            if (!hashId) {
                const message = "Keine hashId verfügbar für die Anfrage.";
                Sentry.captureMessage(message, {
                    level: 'warning',
                    extra: { data }
                });
                throw new Error(message);
            }

            Sentry.addBreadcrumb({
                category: 'api',
                message: `Requesting submit application on next with ${data} and id ${hashId} to finish application`,
                level: 'info'
            });

            try {
                const response = await http.patch(`/api/submit-applications/${hashId}`, data, {
                    headers: {
                        'Content-Type': 'application/merge-patch+json', // Überschreiben des Content-Type für diesen Request da Patch und kein Get
                    },
                });
                this.setHashId(response.data.hashId);
                this.setPrefixedOrderId(response.data.prefixedOrderId);
                return response.data;
            } catch (error) {
                const responseData = error?.response?.data || 'No response data';

                Sentry.captureException(error, {
                    level: 'error',
                    tags: {
                        function: 'postNextData',
                        hashId
                    },
                    extra: {
                        data,
                        responseData
                    }
                });
                throw error;
            }
        },
        scrollToError() {
            const errorElement = document.querySelector('.error-message');
            if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        },
        async validateCreditCheck() {
            const hashId = this.getHashId;
            if (!hashId) {
                const message = "Keine hashId verfügbar für die Anfrage.";
                Sentry.captureMessage(message, {
                    level: 'warning',
                    function: 'validateCreditCheck'
                });
                return;
            }

            Sentry.addBreadcrumb({
                category: 'api',
                message: `Requesting validation of credit check for ${hashId}`,
                level: 'info'
            });

            try {
                const response = await http.get(`/api/validateLastCreditCheck/${hashId}`);
                this.isCreditCheckValid = response.data?.valid;
                return response.data?.valid;
            } catch (error) {
                Sentry.captureException(error, {
                    level: 'error',
                    tags: {
                        function: 'validateCreditCheck',
                        hashId
                    },
                    extra: {
                        hashId
                    }
                });
            }
        },
        async performReCreditCheck() {
            const hashId = this.getHashId;
            if (!hashId) {
                const message = "Keine hashId verfügbar für die Anfrage.";
                Sentry.captureMessage(message, {
                    level: 'warning',
                    function: 'performReCreditCheck'
                });
                return;
            }

            Sentry.addBreadcrumb({
                category: 'api',
                message: `Requesting re-credit check for ${hashId}`,
                level: 'info'
            });

            http.post(`/api/reCreditCheck/${hashId}`)
                .then(response => {
                    if (response.status === 204) {
                    this.isCreditCheckValid = true;
                    this.creditCheckValidUntil = response.data.expiryDate; 
                    }
                })
                .catch(error => {
                    Sentry.captureException(error, {
                        level: 'error',
                        tags: {
                            function: 'performReCreditCheck',
                            hashId
                        },
                        extra: {
                            hashId
                        }
                    });
                });
        },
        handleBackEndValidations(validationErrors, key) {
            const fieldMapping = {
                'firstName': 'firstName',
                'lastName': 'lastName',
                'birthdate': 'birthdate',
                'gender': 'gender',
                'email': 'email',
                'phone': 'phone',
                'tenantAddress.streetName': 'streetName',
                'tenantAddress.houseNumber': 'houseNumber',
                'tenantAddress.addressCode': 'addressCode',
                'tenantAddress.city': 'city',
                'rentAmount': 'rentAmount',
                'moveInDate': 'moveInDate',
                'contractStart': 'contractStart',
                'landlordName': 'landlordName',
                'landlordEmail': 'landlordEmail',
                'IBAN': 'IBAN',
                'depositAmount': 'depositAmount',
                'destAddress': 'destAddress',
                'landlordAddress.streetName': 'landlordStreetName',
                'landlordAddress.houseNumber': 'landlordHouseNumber',
                'landlordAddress.addressCode': 'landlordAddressCode',
                'landlordAddress.city': 'landlordCity',
                'propertyAddress.streetName': 'propertyStreetName',
                'propertyAddress.houseNumber': 'propertyHouseNumber',
                'propertyAddress.addressCode': 'propertyAddressCode',
                'propertyAddress.city': 'propertyCity',
                'sepaTerms': 'sepaTerms',
            };
        
            return Object.fromEntries(validationErrors.map(validation => {
                const mappedField = fieldMapping[validation.propertyPath] || validation.propertyPath;
                return [key + mappedField, validation.message];  // Anwendung des Prefixes `start_` |  `next_` und des gemappten Feldnamens
            }));
        },
        trackFormSubmits() {
            switch (this.redirectionPath) {
                case '/app/next':
                    this.trackSofortzusage();
                    break;
                case '/app/danke-reservierung':
                    this.trackReservation();
                    break;
                case '/app/ablehnung':
                    this.trackAblehnung();
                    break;
                case '/app/danke-guarantee':
                    this.trackEcommerceEvent();
                    break;
                default:
                    break;
            }
        },
        trackEcommerceEvent() {
            if (this.tracking.trackedEcommerce) {
                return;
            }
            if (window.localStorage.getItem('trackedEcommerce')) {
                this.tracking.trackedEcommerce = true;
                return;
            }
            this.tracking.trackedEcommerce = true;
            window.localStorage.setItem('trackedEcommerce', true);
            window.dataLayer.push({
                ecommerce: null
            });
            window.dataLayer.push({
                event: 'purchase',
                ecommerce: {
                    transaction_id: this.prefixedOrderId,
                    value: this.yearlyRate,
                    tax: 0,
                    shipping: 0,
                    currency: "EUR",
                    items: [{
                        item_name: 'Mietkautionsbürgschaft',
                        item_id: this.hashId,
                        price: this.yearlyRate,
                        item_brand: 'heysafe',
                        quantity: '1'
                    }]
                }
            });
        },
        trackReservation() {
            if (this.tracking.trackedReservation) {
                return;
            }
            if (window.localStorage.getItem('trackedReservation')) {
                this.tracking.trackedReservation = true;
                return;
            }
            this.tracking.trackedReservation = true;
            window.localStorage.setItem('trackedReservation', true);
            window.dataLayer.push({
                event: 'Reservierung'
            });
        },
        trackSofortzusage() {
            if (this.tracking.trackedSofortzusage) {
                return;
            }
            if (window.localStorage.getItem('trackedSofortzusage')) {
                this.tracking.trackedSofortzusage = true;
                return;
            }
            this.tracking.trackedSofortzusage = true;
            window.localStorage.setItem('trackedSofortzusage', true);
            window.dataLayer.push({
                event: 'Sofortzusage'
            });
        },
        trackAblehnung() {
            if (this.tracking.trackedAblehnung) {
                return;
            }
            if (window.localStorage.getItem('trackedAblehnung')) {
                this.tracking.trackedAblehnung = true;
                return;
            }
            this.tracking.trackedAblehnung = true;
            window.localStorage.setItem('trackedAblehnung', true);
            window.dataLayer.push({
                event: 'Ablehnung'
            });
        },
        trackPending() {
            if (this.tracking.trackedPending) {
                return;
            }
            if (window.localStorage.getItem('trackedPending')) {
                this.tracking.trackedPending = true;
                return;
            }
            this.tracking.trackedPending = true;
            window.localStorage.setItem('trackedPending', true);

            window.dataLayer.push({
                event: 'Pending'
            });
        },
        getFieldStep(field) { // get the step in the form (start, next, payment)
            const fields = {
                start_gender: 'start',
                start_firstName: 'start',
                start_lastName: 'start',
                start_birthdate: 'start',
                start_email: 'start',
                start_phone: 'start',
                start_streetName: 'start',
                start_houseNumber: 'start',
                start_addressCode: 'start',
                start_city: 'start',
                start_termsAccepted: 'start',
                next_moveInDate: 'next',
                next_contractStart: 'next',
                next_propertyStreetName: 'next',
                next_propertyHouseNumber: 'next',
                next_propertyAddressCode: 'next',
                next_propertyCity: 'next',
                next_landlordStreetName: 'next',
                next_landlordHouseNumber: 'next',
                next_landlordAddressCode: 'next',
                next_landlordCity: 'next',
                next_landlordName: 'next',
                next_landlordEmail: 'next',
                next_rentAmount: 'next',
                next_depositAmount: 'next',
                next_destAddress: 'next',
                next_IBAN: 'payment',
                next_sepaTerms: 'payment',
            }
            return fields[field];
        },
        setFormValue(field, value) {
            this.formValues[field] = value;
            window.localStorage.setItem(field, value);
        },
        setEmail(email) {
            this.email = email;
        },
        setIsReservation(isReservation) {
            this.isReservation = isReservation;
            localStorage.setItem('isReservation', isReservation);
        },
        setHashId(hashId) {
            this.hashId = hashId;
            setCookie('hashId', hashId, 30);
            localStorage.setItem('id', hashId);
        },
        setPrefixedOrderId(prefixedOrderId) {
            this.prefixedOrderId = prefixedOrderId;
        },
        setPartnerId(partnerId) {
            this.partnerId = partnerId;
        },
        setExternalId(externalId) {
            this.externalId = externalId;
        },
        setProcessingRequestedState (requestedState) {
            this.processing.requestedState = requestedState;
            localStorage.setItem('requestedState', requestedState);
        },
        setProcessingModalOpen(isProcessingModalOpen) {
            this.processing.isProcessingModalOpen = isProcessingModalOpen;
        },
        setProcessingShowSpinnerOne(showSpinnerOne) {
            this.processing.showSpinnerOne = showSpinnerOne;
        },
        setProcessingShowSpinnerTwo(showSpinnerTwo) {
            this.processing.showSpinnerTwo = showSpinnerTwo;
        },
        setProcessingShowSpinnerThree(showSpinnerThree) {
            this.processing.showSpinnerThree = showSpinnerThree;
        },
        setProcessingStartState(startState) { //set the state before the processing to see if state changes
            this.processing.startState = startState;
        },
        parseLocalizedNumber(string) {
            if (!string) return 0; 
            const normalizedString = string.replace(',', '.');
            const number = Number(normalizedString);
    
            if (isNaN(number)) {
                return 0; 
            }

            return number;
        },
        resetProcessing() {
            this.processing = {
                tryCount: 0,
                requestedState: '',
                isProcessingModalOpen: false,
                showSpinnerOne: false,
                showSpinnerTwo: false,
                showSpinnerThree: false,
                isIbanModalOpen: false,
            };
        },
        resetLandlordAddress() {
            this.formValues.next_landlordStreetName = '';
            this.formValues.next_landlordHouseNumber = '';
            this.formValues.next_landlordAddressCode = '';
            this.formValues.next_landlordCity = '';
        },
        resetData() {
            setCookie('hashId', '', -1);
            localStorage.removeItem('id');
            this.$reset(); // Setzt den Store-Zustand zurück
        },
    },
});